import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { PLATFORMS, COMMON_CONTENT } from '../../docs/connectSetupContent';

// ---------------------------------------------------------------------------
// Markdown components — docs-optimised styling using app CSS variables
// ---------------------------------------------------------------------------

const mdComponents = {
  h2: ({ children }) => (
    <h2 style={{
      fontSize: '1.15rem',
      fontWeight: 700,
      marginTop: '1.75rem',
      marginBottom: '0.35rem',
      color: 'var(--text-color)',
      borderBottom: '2px solid var(--input-border)',
      paddingBottom: '0.3rem',
    }}>
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 style={{
      fontSize: '0.98rem',
      fontWeight: 600,
      marginTop: '1.25rem',
      marginBottom: '0.25rem',
      color: 'var(--text-color)',
    }}>
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p style={{ margin: '0.45rem 0', lineHeight: 1.65, color: 'var(--text-color)' }}>
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul style={{ margin: '0.35rem 0 0.5rem 1.2rem', padding: 0, color: 'var(--text-color)' }}>
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol style={{ margin: '0.35rem 0 0.5rem 1.2rem', padding: 0, color: 'var(--text-color)' }}>
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li style={{ margin: '0.2rem 0', lineHeight: 1.6 }}>{children}</li>
  ),
  code: ({ inline, children }) => {
    if (inline) {
      return (
        <code style={{
          background: 'var(--input-background)',
          border: '1px solid var(--input-border)',
          borderRadius: '3px',
          padding: '0.1em 0.35em',
          fontSize: '0.83em',
          fontFamily: 'monospace',
          color: 'var(--text-color)',
          wordBreak: 'break-all',
        }}>
          {children}
        </code>
      );
    }
    return <code style={{ fontFamily: 'monospace' }}>{children}</code>;
  },
  pre: ({ children }) => (
    <pre style={{
      background: 'var(--input-background)',
      border: '1px solid var(--input-border)',
      borderRadius: '6px',
      padding: '0.7rem 0.9rem',
      overflowX: 'auto',
      fontSize: '0.8rem',
      lineHeight: 1.55,
      margin: '0.55rem 0',
      fontFamily: 'monospace',
      color: 'var(--text-color)',
      whiteSpace: 'pre',
    }}>
      {children}
    </pre>
  ),
  hr: () => (
    <hr style={{ border: 'none', borderTop: '1px solid var(--input-border)', margin: '1.1rem 0' }} />
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'var(--accent-color, #4a9eff)', textDecoration: 'underline' }}
    >
      {children}
    </a>
  ),
  strong: ({ children }) => (
    <strong style={{ fontWeight: 600, color: 'var(--text-color)' }}>{children}</strong>
  ),
  blockquote: ({ children }) => (
    <blockquote style={{
      borderLeft: '3px solid var(--input-border)',
      margin: '0.65rem 0',
      paddingLeft: '0.8rem',
      color: 'var(--secondary-text-color)',
      fontStyle: 'italic',
    }}>
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div style={{ overflowX: 'auto', margin: '0.65rem 0' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.86rem', lineHeight: 1.5 }}>
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead style={{ background: 'var(--input-background)' }}>{children}</thead>
  ),
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => (
    <tr style={{ borderBottom: '1px solid var(--input-border)' }}>{children}</tr>
  ),
  th: ({ children }) => (
    <th style={{
      padding: '5px 10px',
      textAlign: 'left',
      fontWeight: 600,
      border: '1px solid var(--input-border)',
      whiteSpace: 'nowrap',
      color: 'var(--text-color)',
    }}>
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td style={{
      padding: '5px 10px',
      border: '1px solid var(--input-border)',
      verticalAlign: 'top',
      color: 'var(--text-color)',
    }}>
      {children}
    </td>
  ),
};

// ---------------------------------------------------------------------------
// SetupGuideModal
// ---------------------------------------------------------------------------

/**
 * Full-viewport setup guide modal with hardware platform selector.
 * Users pick their platform (Apple Silicon, NVIDIA GPU, Jetson/Spark,
 * Windows) and see only the steps relevant to that hardware, followed
 * by common sections (configuration, management, troubleshooting).
 *
 * @param {Function} props.onClose - Called when the user dismisses the modal.
 */
const SetupGuideModal = ({ onClose }) => {
  const [activePlatform, setActivePlatform] = useState(PLATFORMS[0].id);

  const handleEsc = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = prev;
    };
  }, [handleEsc]);

  const platform = PLATFORMS.find((p) => p.id === activePlatform);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 9000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--modal-background, var(--panel-background))',
          width: '100%',
          maxWidth: '800px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 40px var(--modal-shadow, rgba(0,0,0,0.35))',
          margin: '2vh 1rem',
          borderRadius: '10px',
          overflow: 'hidden',
          maxHeight: '94vh',
        }}
      >
        {/* ---------------------------------------------------------------- */}
        {/* Header                                                            */}
        {/* ---------------------------------------------------------------- */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.9rem 1.25rem',
          borderBottom: '1px solid var(--input-border)',
          flexShrink: 0,
          background: 'var(--modal-background, var(--panel-background))',
        }}>
          <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-color)' }}>
            Stackform Connect — Setup Guide
          </span>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.4rem',
              lineHeight: 1,
              cursor: 'pointer',
              color: 'var(--secondary-text-color)',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            ×
          </button>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Platform picker                                                   */}
        {/* ---------------------------------------------------------------- */}
        <div style={{
          padding: '0.85rem 1.25rem 0',
          borderBottom: '1px solid var(--input-border)',
          flexShrink: 0,
          background: 'var(--modal-background, var(--panel-background))',
        }}>
          <p style={{
            fontSize: '0.78rem',
            color: 'var(--secondary-text-color)',
            margin: '0 0 0.6rem 0',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Select your hardware
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', paddingBottom: '0.85rem' }}>
            {PLATFORMS.map((p) => {
              const isActive = p.id === activePlatform;
              return (
                <button
                  key={p.id}
                  onClick={() => setActivePlatform(p.id)}
                  style={{
                    padding: '0.45rem 0.85rem',
                    borderRadius: '6px',
                    border: `1px solid ${isActive ? 'var(--text-color)' : 'var(--input-border)'}`,
                    background: isActive ? 'var(--text-color)' : 'var(--input-background)',
                    color: isActive ? 'var(--panel-background)' : 'var(--text-color)',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: isActive ? 600 : 400,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    lineHeight: 1.3,
                    transition: 'all 0.15s',
                  }}
                >
                  <span>{p.label}</span>
                  <span style={{
                    fontSize: '0.7rem',
                    opacity: isActive ? 0.75 : 0.55,
                    fontWeight: 400,
                    marginTop: '1px',
                  }}>
                    {p.subtitle}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Scrollable content                                                */}
        {/* ---------------------------------------------------------------- */}
        <div style={{ overflowY: 'auto', padding: '0.5rem 1.5rem 2rem', flex: 1, fontSize: '0.88rem' }}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeSanitize]}
            components={mdComponents}
          >
            {platform.content}
          </ReactMarkdown>

          {/* Divider before common sections */}
          <div style={{
            margin: '2rem 0 0',
            borderTop: '2px solid var(--input-border)',
            paddingTop: '0.25rem',
          }} />

          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeSanitize]}
            components={mdComponents}
          >
            {COMMON_CONTENT}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default SetupGuideModal;
